import { defineComponent, ref, onUnmounted, watch } from 'vue';
import { onAuthStateChanged } from 'firebase/auth';
import { useRoute } from 'vue-router';
import { getDoc } from 'firebase/firestore';
import { auth, getUserRole, getIdTokenClaims, userDocRef } from '../firebase';
import { ChartNoAxesCombined, ClipboardPenLine, ClipboardList, Users, Package, PackageCheck, Gauge, CircleArrowLeft, CircleQuestionMark, Shapes, Plus, Hammer, ChevronDown, ChevronUp, Building2, Truck, Printer, Tickets, PackagePlus, LayoutGrid, ShoppingCart, Bell, ChevronLeft, ChevronRight } from 'lucide-vue-next';
export default defineComponent({
    name: "AdminLayout",
    components: {
        ChartNoAxesCombined,
        ClipboardPenLine,
        ClipboardList,
        Users,
        Package,
        PackageCheck,
        Gauge,
        CircleArrowLeft,
        CircleQuestionMark,
        Shapes,
        Plus,
        Hammer,
        ChevronDown,
        ChevronUp,
        Building2,
        Truck,
        Printer,
        Tickets,
        PackagePlus,
        LayoutGrid,
        ShoppingCart,
        Bell,
        ChevronLeft,
        ChevronRight
    },
    setup() {
        // keep sections open if current route belongs to them
        const route = useRoute();
        const ordersOpen = ref(route.path.startsWith('/app/orders'));
        const manageOpen = ref(route.path.startsWith('/app/manage'));
        const productsOpen = ref(route.path.startsWith('/app/products'));
        const sidebarCollapsed = ref(false);
        const role = ref(null);
        const loading = ref(true);
        // simple placeholders for badges (replace with real data as needed)
        const cartCount = ref(0);
        const notificationsCount = ref(0);
        // profile info
        const displayName = ref(null);
        const photoURL = ref(null);
        const currentUid = ref(null);
        // Toggle functions
        const toggleOrders = () => (ordersOpen.value = !ordersOpen.value);
        const toggleManage = () => (manageOpen.value = !manageOpen.value);
        const toggleProducts = () => (productsOpen.value = !productsOpen.value);
        // keep sidebar sections in sync with navigation
        watch(() => route.path, (p) => {
            // open the matching group for the current route, keep manual toggles otherwise
            if (p.startsWith('/app/orders')) {
                ordersOpen.value = true;
            }
            if (p.startsWith('/app/manage')) {
                manageOpen.value = true;
            }
            if (p.startsWith('/app/products')) {
                productsOpen.value = true;
            }
        }, { immediate: false });
        // Listen for auth and fetch role from Firestore
        const unsub = onAuthStateChanged(auth, async (user) => {
            loading.value = true;
            console.debug('[AdminLayout] onAuthStateChanged user=', user?.uid ?? null);
            if (!user) {
                role.value = null;
                displayName.value = null;
                photoURL.value = null;
                currentUid.value = null;
                loading.value = false;
                return;
            }
            try {
                currentUid.value = user.uid;
                // log id token claims
                await getIdTokenClaims().catch(() => { });
                const r = await getUserRole(user.uid);
                console.debug('[AdminLayout] getUserRole returned', r);
                role.value = r ?? null;
                // Prefer Firestore user's displayName / photoURL when present
                try {
                    const snap = await getDoc(userDocRef(user.uid));
                    if (snap.exists()) {
                        const data = snap.data();
                        displayName.value = data.displayName ?? user.displayName ?? user.email ?? null;
                        photoURL.value = data.photoURL ?? user.photoURL ?? null;
                    }
                    else {
                        displayName.value = user.displayName ?? user.email ?? null;
                        photoURL.value = user.photoURL ?? null;
                    }
                }
                catch (e) {
                    // fallback to auth user fields on any error
                    displayName.value = user.displayName ?? user.email ?? null;
                    photoURL.value = user.photoURL ?? null;
                }
            }
            catch (e) {
                console.error('[AdminLayout] Failed to load user role', 'code=', e?.code, e?.message, e);
                role.value = null;
            }
            finally {
                loading.value = false;
            }
        });
        onUnmounted(() => {
            if (typeof unsub === 'function')
                unsub();
        });
        return {
            sidebarCollapsed,
            ordersOpen, manageOpen, productsOpen,
            toggleOrders, toggleManage, toggleProducts,
            role, loading,
            cartCount, notificationsCount,
            displayName, photoURL,
            currentUid
        };
    }
});
